/**
 * column-match.v1 cizici — koddan cizilen renkli sutunlar (birak hedefi) + yildiz
 * kartlari (surukle). Cocuk her karti kurala gore dogru sutunun altina birakir
 * (uzun sutun = cok yildiz). Sutunlar ve yildizlar KODDAN cizilir.
 *
 * Icerik: { prompt?, columns:[{id,color,height}], cards:[{id,stars}] }
 *   (dogru sutun `col` yalnizca authoring'de; client'a gelmez.)
 * Cevap: onAnswerChange({ placements:{ <cardId>:<colId> } }); hepsi yerlesene
 *   kadar null. Skorlama SUNUCUDA (kart.stars siralamasi ~ sutun.height).
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { MechanicProps } from "./types";

interface CMColumn {
  id: string;
  color: string;
  height: number;
}
interface CMCard {
  id: string;
  stars: number;
}
interface CMContent {
  prompt?: { text?: string; subText?: string };
  columns?: CMColumn[];
  cards?: CMCard[];
}

function StarCard({ n }: { n: number }) {
  return (
    <span className="cm-stars">
      {Array.from({ length: n }).map((_, i) => (
        <svg key={i} viewBox="0 0 24 24" className="cm-star" aria-hidden="true">
          <polygon
            points="12,2 14.7,8.6 21.8,9.2 16.4,13.8 18.2,20.8 12,17 5.8,20.8 7.6,13.8 2.2,9.2 9.3,8.6"
            fill="#f5a623"
            stroke="#d98a00"
            strokeWidth="1"
            strokeLinejoin="round"
          />
        </svg>
      ))}
    </span>
  );
}

export function ColumnMatchItem({ item, onAnswerChange }: MechanicProps) {
  const c = item.content as unknown as CMContent;
  const columns = c.columns ?? [];
  const cards = c.cards ?? [];
  const byId: Record<string, CMCard> = {};
  cards.forEach((k) => (byId[k.id] = k));

  const saved = item.answer?.placements as Record<string, string> | undefined;
  const [placements, setPlacements] = useState<Record<string, string>>(saved ?? {});
  const [drag, setDrag] = useState<{ id: string; x: number; y: number } | null>(null);

  const cb = useRef(onAnswerChange);
  cb.current = onAnswerChange;
  useEffect(() => {
    const done = cards.length > 0 && cards.every((k) => placements[k.id]);
    cb.current(done ? { placements } : null, Object.keys(placements).length > 0 ? { placements } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placements]);

  const maxH = Math.max(1, ...columns.map((x) => x.height || 0));

  function place(cardId: string, colId: string) {
    setPlacements((prev) => {
      const next = { ...prev };
      const occ = Object.keys(next).find((k) => next[k] === colId);
      if (occ && occ !== cardId) delete next[occ]; // sütun tek kart alır; eski havuza döner
      next[cardId] = colId;
      return next;
    });
  }
  function unplace(cardId: string) {
    setPlacements((prev) => {
      const n = { ...prev };
      delete n[cardId];
      return n;
    });
  }

  useEffect(() => {
    if (!drag) return;
    const move = (e: PointerEvent) =>
      setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : d));
    const up = (e: PointerEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const drop = el?.closest("[data-drop]") as HTMLElement | null;
      const pool = el?.closest("[data-pool]") as HTMLElement | null;
      if (drop) place(drag.id, drop.dataset.drop!);
      else if (pool) unplace(drag.id);
      setDrag(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);

  function startDrag(e: React.PointerEvent, id: string) {
    e.preventDefault();
    setDrag({ id, x: e.clientX, y: e.clientY });
  }

  const poolCards = cards.filter((k) => !placements[k.id]);
  const cardInCol = (colId: string) => {
    const id = Object.keys(placements).find((k) => placements[k] === colId);
    return id ? byId[id] : null;
  };
  const dragCard = drag ? byId[drag.id] : null;

  return (
    <div className="mech cm-mech">
      {c.prompt?.text && <h2 className="prompt">{c.prompt.text}</h2>}
      {c.prompt?.subText && <p className="prompt-sub">{c.prompt.subText}</p>}

      <div className="cm-columns">
        {columns.map((col) => {
          const card = cardInCol(col.id);
          return (
            <div className="cm-col" key={col.id}>
              <div className="cm-bar-wrap">
                <div
                  className="cm-bar"
                  style={{ height: `${(col.height / maxH) * 100}%`, background: col.color }}
                />
              </div>
              <div className="cm-drop" data-drop={col.id}>
                {card && (
                  <button
                    type="button"
                    className={`cm-card placed${drag?.id === card.id ? " dragging" : ""}`}
                    onPointerDown={(e) => startDrag(e, card.id)}
                  >
                    <StarCard n={card.stars} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="cm-pool" data-pool="1">
        {poolCards.map((k) => (
          <button
            type="button"
            key={k.id}
            className={`cm-card${drag?.id === k.id ? " dragging" : ""}`}
            onPointerDown={(e) => startDrag(e, k.id)}
          >
            <StarCard n={k.stars} />
          </button>
        ))}
      </div>

      {/* hayalet body'ye cizilir: soru alani zoom ile kucultulunce fixed koordinatlar kayiyor, yildizlar bozuk gorunuyordu */}
      {drag && dragCard && createPortal(
        <div className="cm-ghost" style={{ left: drag.x, top: drag.y }}>
          <StarCard n={dragCard.stars} />
        </div>,
        document.body
      )}
    </div>
  );
}
